<template>
  <v-container id="evaluations" fluid tag="section">
    <base-v-component :heading="$t('evaluations')" :subtitle="$t('evaluations-subtitle')" />

    <ImportSheet 
      v-model="dialogImport"
    />

    <AutoMap
      v-model="dialogAutomap"
    />

    <div class="d-flex align-baseline">

        <v-tooltip bottom class="flex-column">
          <template v-slot:activator="{ on, attrs }">
            <v-btn 
              fab
              small
              color="primary"
              v-bind="attrs"
              v-on="on"
              @click="onCreateItem"
            >
              <v-icon>mdi-plus</v-icon>
            </v-btn>
            </template>
          <span>{{ $t('ingredient-create-new') }}</span>
        </v-tooltip>

        <v-tooltip bottom class="flex-column">
          <template v-slot:activator="{ on, attrs }">
            <v-btn 
              fab
              small
              color="primary"
              v-bind="attrs"
              v-on="on"
              :disabled="!selected.length"
              @click="dialogConfirmDelete=true"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
            </template>
          <span>{{ $t('delete') }}</span>
        </v-tooltip>

        <v-tooltip bottom class="flex-column flex-md-row">
          <template v-slot:activator="{ on, attrs }">
            <v-btn 
              fab
              small
              color="primary"
              v-bind="attrs"
              v-on="on"
              @click="onImport"
            >
              <v-icon>mdi-import</v-icon>
            </v-btn>
            </template>
          <span>{{ $t('import') }}</span>
        </v-tooltip>

        <v-tooltip bottom class="flex-column flex-md-row">
          <template v-slot:activator="{ on, attrs }">
            <v-btn 
              fab
              small
              color="primary"
              v-bind="attrs"
              v-on="on"
              @click="onAutomap"
            >
              <v-icon>mdi-link</v-icon>
            </v-btn>
            </template>
          <span>{{ $t('automap-ingrediens') }}</span>
        </v-tooltip>

        <!--
        <v-text-field
          v-model="search"
          class="flex-column pl-2"
          append-icon="mdi-magnify"
          label="Search"
          single-line
          hide-details
        />
        -->
        <v-autocomplete
          v-model="searchSelectId"
          :loading="searchLoading"
          :disabled="totalItems<=10"
          :items="searchItems"
          item-text="inciName"
          item-value="objectID"
          cache-items
          class="flex-column pl-2"
          flat
          hide-no-data
          hide-details
          append-icon="mdi-magnify"
          label="Search"
          solo-inverted
          @update:search-input="querySearch"
        >
          <template
            slot="item"
            slot-scope="data"
          >
            {{data.item.casNumber}} - {{ data.item.inciName }}
          </template>
        </v-autocomplete><!-- :search-input.sync="search" -->
    </div>

    <v-card>
      <v-data-table
        :headers="headers"
        :items="projectIngredients"
        :loading="fetchLoading"
        sort-by="updated"
        sort-desc
        :options.sync="tableOptions"
        class="elevation-1 pa-3 clickable"
        :dense="false"
        :calculate-widths="true"
        :show-select="true"
        :hide-default-footer="true"
        :disable-pagination="true"
        @click:row="onRowClick"
        v-model="selected"
      >
        <template v-slot:item.updated="{ item }">{{ $moment(new Date(item.updated)).format("YYYY-MM-DD") }}</template>
        <template v-slot:item.notes="{ item }">
          <v-tooltip v-if="!item.assessment" bottom>
            <template #activator="{ on }">
              <v-icon v-on="on" small class="mr-2" color="accent">mdi-exclamation-thick</v-icon>
            </template>
            <span>Evaluated, but lacks safety assessment</span>
          </v-tooltip>
          <v-tooltip v-if="isProhibited(item)" bottom>
            <template #activator="{ on }">
              <v-icon v-on="on" small class="mr-2" color="red">mdi-alert</v-icon>
            </template>
            <span>Ingredient is prohibited (Annex 2)</span>
          </v-tooltip>
          <v-tooltip v-if="isRestricted(item)" bottom>
            <template #activator="{ on }">
              <v-icon v-on="on" small class="mr-2">mdi-alert-circle-outline</v-icon>
            </template>
            <span>Ingredient has restriction (Annex 3)</span>
          </v-tooltip>
        </template>
        <template v-slot:item.options="{item}">
          <v-menu 
            transition="slide-x-transition"
            bottom
            right
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
                class="no-background primary--text"
              >
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item
                v-for="(option, index) in options"
                :key="index"
                @click="onOption(option.id, item)"
              >
                <v-list-item-title>{{ option.title }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
        <template v-slot:footer>
          <section class="my-6 text-center">
              <v-btn
                v-if="totalItems>projectIngredients.length && !fetchLoading"
                color="primary"
                text
                @click="loadMore()"
                :disabled="fetchLoading || totalItems<=projectIngredients.length"
              >
                Load more
              </v-btn>
          </section>
        </template>
      </v-data-table>
    </v-card>

    <!-- DIALOG CONFIRM DELETE -->
    <v-dialog v-model="dialogConfirmDelete" persistent max-width="290">
      <v-card>
        <v-card-title class="headline">{{ $t('delete-multiple-items') }}</v-card-title>
        <v-card-text>{{ $t('delete-multiple-items-confirm') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogConfirmDelete = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="error" text @click="onDeleteMultipleItems">{{ $t('delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DIALOG CONFIRM DELETE ITEM -->
    <v-dialog v-model="dialogDeleteItem" persistent max-width="290">
      <v-card>
        <v-card-title class="headline">{{ $t('delete-project-ingredient') }}</v-card-title>
        <v-card-text>{{ $t('delete-project-ingredient-confirm') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogDeleteItem = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="error" text @click="onDeleteItem">{{ $t('delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import ImportSheet from '@/components/ImportSheet'
import AutoMap from '@/components/AutoMap'

export default {
  name: "Evaluations",
  components: {
    ImportSheet,
    AutoMap,
  },
  data: () => ({
    selected: [],
    headers: [
      { text: "INCI Name", value: "inciName" },
      { text: "CAS", value: "casNumber", width: "23%" },
      { text: "Updated", value: "updated", width: "12%" },
      {
        text: "",
        value: "notes",
        sortable: false,
        width: "10%",
        align: "right",
      },
      {
        text: "",
        value: "options",
        sortable: false,
        width: "2%",
        align: "right",
      },
    ],
    options: [
      {
        title: 'Edit',
        id: 'edit'
      },
      {
        title: 'Delete',
        id: 'delete'
      },
    ],

    sortedBy: 'updated',
    sortedDesc: true,

    // pagination
    tableOptions: {},
    fetchLoading: false,
    totalItems: 0,
    limit: 10,

    isLoading: false,
    item: null,

    // search
    searchLoading: false,
    searchItems: [],
    search: null,
    searchSelectId: null,

    dialogConfirmDelete: false,
    dialogImport: false,
    dialogAutomap: false,
    dialogDeleteItem: false,
  }),

  created() {
    if (this.activeProject && this.activeProject.id) {
      this.setTotalProjectItems(this.activeProject)
      if (this.projectIngredients.length<this.limit) {
        this.loadMore();
      }
    }
  },

  watch: {
    tableOptions: {
      handler () {
        if (this.activeProject && this.activeProject.id) {
          if (this.tableOptions && this.tableOptions.sortBy && this.tableOptions.sortDesc) {
            if (this.tableOptions.sortBy[0] != this.sortedBy || this.tableOptions.sortDesc[0] != this.sortedDesc) {
              this.sortedBy = this.tableOptions.sortBy[0];
              this.sortedDesc = this.tableOptions.sortDesc[0]
              this.clearFetch();
            }
          }
        }
      },
      deep: true,
    },
    activeProject: function () {
      this.setTotalProjectItems();
      this.clearFetch();
    },
    searchSelectId: function (newId) {
      if (newId) {
        this.$router.push({
          name: "evaluations.edit",
          params: { id: newId },
        });
      }
    },
  },

  computed: {
    subscription() {
      return this.$store.getters['company/getSubscription']
    },
    activeProject() {
      return this.$store.getters["projects/getActiveProject"];
    },
    projectIngredients() {
      return this.$store.getters["projectIngredients/getProjectIngredients"];
    },
    customIngredients() {
      return this.$store.getters[
        "projectIngredients/getCustomProjectIngredients"
      ];
    },
  },

  methods: {
    setTotalProjectItems() {
      const project = this.activeProject;
      this.totalItems = project.quantity && project.quantity.ingredients ? project.quantity.ingredients : 0;
    },
    loadMore() {
      if (this.totalItems>this.projectIngredients.length) {
        this.fetchProjectIngredients();
      }
    },
    async clearFetch() {
      await this.$store.dispatch("projectIngredients/clearProjectIngredients")
      this.fetchProjectIngredients();
    },
    fetchProjectIngredients() {
      this.fetchLoading = true;
      this.$store.dispatch("projectIngredients/fetchProjectIngredients", { projectId: this.activeProject.id, options: this.tableOptions })
        .then(() => {
          this.fetchLoading = false;
        })
        .catch((error) => {
          console.log(error)
          this.fetchLoading = false;
        })
    },
    async querySearch(search) {
      if (search && search.length >= 2) {
        this.$searchService.querySearch('project_ingredients', search)
          .then((res) => {
            this.searchItems = res;
          })
      }
    },
    isProhibited (item) {
      return this.$store.getters["ingredients/isProhibited"](item);
    },
    isRestricted (item) {
      return this.$store.getters["ingredients/isRestricted"](item);
    },
    onRowClick(item) {
      this.onEditItem(item);
    },
    onOption(option, item) {
      if (option === 'edit') {
        this.onEditItem(item)
      }
      else if (option==='delete') {
        this.item = item;
        this.dialogDeleteItem = true;
      }
    },
    onImport() {
      if (!this.subscription || (this.subscription && !this.subscription.premium)) {
        this.$emit('showSnackbar', {
          text: "This functionality is only available for premium accounts.",
          button: "Upgrade",
          routeTo: "account",
        });
        return null;
      }

      this.dialogImport = true;
    },
    onAutomap() {
      if (!this.subscription || (this.subscription && !this.subscription.premium)) {
        this.$emit('showSnackbar', {
          text: "This functionality is only available for premium accounts.",
          button: "Upgrade",
          routeTo: "account",
        });
        return null;
      }

      this.dialogAutomap = true;
    },
    onCreateItem() {
      if (this.activeProject.id) {
        this.$router.push({ name: "evaluations.create" });
      } else {
        this.$notify({
          group: "base",
          type: "error",
          title: "No active project selected",
          text: "Select a project before creating an ingredient.",
        });
      }
    },
    onEditItem(item) {
      this.$router.push({
        name: "evaluations.edit",
        params: { id: item.id },
      });
    },
    onDeleteItem () {
      this.$store.dispatch('projectIngredients/deleteProjectIngredient', this.item)
        .then(() => {
          this.$notify({
            group: "base",
            type: "success",
            title: "Item delete",
            text: "The item has been successfully deleted.",
          });
        })
        .catch((error) => {
          console.log(error);
          this.$notify({
            group: "base",
            type: "error",
            title: "Oops, something went wrong",
            text: "The item has not been deleted. Please try again.",
          });
        });
      this.dialogDeleteItem = false;
    },
    onDeleteMultipleItems () {
      if (this.activeProject.id) {
        this.$store
          .dispatch(
            "projectIngredients/deleteMultipleEvaluations",
            this.selected
          )
          .then(() => {
            this.$notify({
              group: "base",
              type: "success",
              title: "Items deleted",
              text: "The selected items has been successfully deleted.",
            });
            this.dialogConfirmDelete = false;
          })
          .catch((error) => {
            console.log(error);
            this.$notify({
              group: "base",
              type: "error",
              title: "Oops, something went wrong",
              text: "The item has not been deleted. Please try again.",
            });
          });
      }
    },
  },
};
</script>

<!--
<style>
.v-select.v-select--is-menu-active .v-input__icon--append .v-icon {
  transform: rotate(0) !important;
}
</style>
-->